import { useTranslation } from 'react-i18next';
import { MenuItem, TextField } from '../primitives';

export interface FilterOption {
  value: string;
  label: string;
  /** Hides the option without removing it — a scope the reader may not filter by. */
  disabled?: boolean;
}

export interface FilterSelectProps<Value extends string> {
  label: string;
  value: Value;
  onChange: (value: Value) => void;
  options: FilterOption[];
  /**
   * Adds a leading "everything" option under this value, `'all'` by default.
   * Pass `null` for a filter with no such state — a required scope, say.
   */
  allValue?: string | null;
  /** Overrides the "All" wording where the noun matters ("All scopes"). */
  allLabel?: string;
  disabled?: boolean;
  /**
   * Wider than the standard step, for a column of municipal names.
   *
   * Use it sparingly. The point of a fixed width is that a row of filters lines
   * up with the row of filters on the next screen.
   */
  wide?: boolean;
}

/**
 * The one filter control above a table.
 *
 * **Why a component for a `<TextField select>`.** Twelve screens wrote this out
 * by hand and no two agreed. The widths ran 130, 150, 170, 180, 190 and 220px,
 * so a filter bar visibly changed shape as you moved between registries. The
 * "everything" option was labelled from five different translation keys — "All",
 * "All scopes", "All statuses" — and two screens omitted it entirely, leaving no
 * way back to the unfiltered list once you had picked something. None of that
 * was a per-screen decision; it was twelve copies of one control drifting.
 *
 * One width, one "All", one place to change either.
 */
export function FilterSelect<Value extends string>({
  label,
  value,
  onChange,
  options,
  allValue = 'all',
  allLabel,
  disabled,
  wide,
}: FilterSelectProps<Value>) {
  const { t } = useTranslation();
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as Value)}
      sx={{ minWidth: wide ? 240 : 180, flexShrink: 0 }}
    >
      {allValue !== null && (
        <MenuItem value={allValue}>{allLabel ?? t('common.filter.all')}</MenuItem>
      )}
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
